import CheckIcon from '@mui/icons-material/Check'
import { Box, Button, Chip, Grid, Stack, Typography } from '@mui/material'
import { PlanChoice } from './PlanSelectionSection'

interface Step1PlanChoiceProps {
  planChoice: PlanChoice
  onSelect: (choice: NonNullable<PlanChoice>) => void
}

const freeBullets = [
  'One-time water test + results',
  'Basic recommendations',
  'View your utility data',
]
const proBullets = [
  'Quarterly testing, monitoring & alerts',
  'Matched filters + annual replacements',
  'Priority support',
]

// SIPP-831: prominent checkmark badge so the selected card is unambiguous
// at a glance — filled primary circle when selected, empty grey outline when
// not. Paired with the existing 2px primary border on the selected card.
const SelectionBadge = ({ selected }: { selected: boolean }) =>
  selected ? (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        top: 12,
        right: 12,
      }}
    >
      <CheckIcon sx={{ color: 'common.white', fontSize: 16 }} />
    </Box>
  ) : (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        border: '2px solid',
        borderColor: 'grey.300',
        position: 'absolute',
        top: 12,
        right: 12,
      }}
    />
  )

export const Step1PlanChoice = ({ planChoice, onSelect }: Step1PlanChoiceProps) => {
  const proSelected = planChoice === 'pro'
  const freeSelected = planChoice === 'free'
  // Pro card keeps its highlighted treatment (primary border + chip) while
  // the wizard is in its untouched starting state — that's the
  // "Recommended" framing. Once the user picks Free, Pro reverts to the
  // neutral unselected look.
  const proHighlighted = proSelected || planChoice === null

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }} order={{ xs: 2, md: 1 }}>
        <Box
          sx={{
            p: 3,
            borderRadius: 1.5,
            border: freeSelected ? '2px solid' : '1px solid',
            borderColor: freeSelected ? 'primary.dark' : 'divider',
            bgcolor: freeSelected ? 'background.neutral' : 'background.paper',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            position: 'relative',
          }}
        >
          <SelectionBadge selected={freeSelected} />
          <Typography variant="h5">Free</Typography>
          <Stack direction="row" alignItems="baseline" spacing={0.5}>
            <Typography variant="h3" fontWeight={700}>
              $0
            </Typography>
            <Typography variant="caption" color="text.secondary">
              forever
            </Typography>
          </Stack>
          <Stack spacing={0.75} sx={{ flex: 1 }}>
            {freeBullets.map((line) => (
              <Stack key={line} direction="row" spacing={1} alignItems="flex-start">
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'text.secondary',
                    mt: 0.9,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2">{line}</Typography>
              </Stack>
            ))}
          </Stack>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => onSelect('free')}
            sx={{ borderColor: 'divider', color: 'text.primary' }}
          >
            Stay on Free
          </Button>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }} order={{ xs: 1, md: 2 }}>
        <Box
          sx={{
            p: 3,
            borderRadius: 1.5,
            border: proHighlighted ? '2px solid' : '1px solid',
            borderColor: proHighlighted ? 'primary.dark' : 'divider',
            bgcolor: proSelected ? 'background.neutral' : 'background.paper',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            position: 'relative',
          }}
        >
          <SelectionBadge selected={proSelected} />
          {/* Chip flips between "Selected" (after the user clicks) and
              "Recommended" (initial state). Hidden once the user has chosen
              Free so the Pro card can recede visually. */}
          {proHighlighted && (
            <Chip
              label={proSelected ? 'Selected' : 'Recommended'}
              size="small"
              sx={{
                bgcolor: 'primary.main',
                color: 'common.white',
                fontWeight: 600,
                alignSelf: 'flex-start',
              }}
            />
          )}
          <Typography variant="h5">SIPP Pro</Typography>
          <Stack direction="row" alignItems="baseline" spacing={0.5}>
            <Typography variant="h3" fontWeight={700}>
              $99
            </Typography>
            <Typography variant="caption" color="text.secondary">
              /year · less than $9/month
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            30-day money-back guarantee
          </Typography>
          <Stack spacing={0.75} sx={{ flex: 1 }}>
            {proBullets.map((line) => (
              <Stack key={line} direction="row" spacing={1} alignItems="flex-start">
                <CheckIcon sx={{ color: 'success.main', fontSize: 18, mt: 0.25 }} />
                <Typography variant="body2">{line}</Typography>
              </Stack>
            ))}
          </Stack>
          <Button variant="contained" fullWidth onClick={() => onSelect('pro')}>
            Choose SIPP Pro
          </Button>
        </Box>
      </Grid>
    </Grid>
  )
}
